<script setup>
  const { mobility: mob } = useCareerSite()
  const { href } = useRequestURL()
  const { fullPath, params, query } = useRoute()
  const config = useRuntimeConfig()
  const { findOne } = useStrapi4()
  const client = useStrapiClient()
  const dayjs = useDayjs()

  const mobility = mob.value || 'mobility' in query

  const careerSite = reactive({
    ...(await useAsyncData(`career-${params.slug}-jobs`, async () => {
      const res = await findOne('career-sites', params.slug, {
        fields: '*',
        populate: {
          cover: { fields: ['url'] },
          employer: {
            fields: ['name', 'slug'],
            populate: {
              cover: { fields: ['url'] },
              logo: { fields: ['url'] },
              seo: {
                fields: ['metaDescription', 'metaTitle', 'structuredData'],
                populate: { metaImage: { fields: ['url'] } }
              }
            }
          }
        }
      })
      return res.data
    }))
  })

  const careerData = computed(() => careerSite.data ?? {})
  const apply = ref(false)

  const jobFetcher = reactive({
    ...(await useAsyncData(`career-${params.job}`, async () => {
      const res = await findOne('jobs', params.job, {
        fields: '*',
        populate: {
          employer: {
            fields: ['name', 'slug', 'website','acronym'],
            populate: {
              cover: { fields: ['url'] },
              logo: { fields: ['url'] }
            }
          },
          locations: { fields: ['city'] },
          sectors: { fields: ['name'] },
          speaker: {
            fields: ['firstName', 'lastName', 'title'],
            populate: {
              photo: { fields: ['url'] }
            }
          },
          jobSheet: { fields: ['url'] }
        }
      })
      return res.data
    }))
  })

  const job = computed(() => {
    const j = jobFetcher.data
    if (!j) return null
    return {
      ...j,
      isExpired() {
        return j.expiresAt < dayjs().format('YYYY-MM-DD')
      },
      isExternal() {
        return !!j.externalUrl
      },
      isStatus(status) {
        return status === j.status
      }
    }
  })

  if (job.value) {
    const j = job.value

    if (process.client && !query.preview) {
      client(`/jobs/${j.id}/view`, {
        body: { url: href },
        method: 'POST'
      }).catch(error => handleError(error))
    }

    const referrer = useReferrer()
    if (process.client) {
      let originUrl = sessionStorage.getItem(`job:${j.id}`)
      if (!originUrl && referrer.value) {
        originUrl = referrer.value
        sessionStorage.setItem(`job:${j.id}`, originUrl)
      }
      console.log('referrer:', originUrl)
      referrer.value = null
    }

    const seo = j.seo ?? {}
    const metaTitle = seo.metaTitle ?? `${j.title} - ${j.employer?.name}`
    useSeo({
      canonicalUrl: config.public.baseUrl + fullPath.split('?')[0],
      metaTitle,
      metaDescription: seo.metaDescription ?? j.answer2,
      metaRobots: mobility ? 'noindex' : seo.metaRobots,
      structuredData: !j.isExpired() ? seo.structuredData : null
    })

    useHead({
      bodyAttrs: {
        class: 'font-sans text-base'
      },
      htmlAttrs: {
        class: 'md:text-[18px]'
      },
      titleTemplate: metaTitle,
      meta: [{ hid: 'og:image', property: 'og:image', content: useStrapiMedia(seo.metaImage?.url ?? j.employer?.cover?.url) }]
    })
  }

  definePageMeta({ layout: false })
</script>

<template>
  <Loading class="mx-auto max-w-screen-xl px-4 py-16" v-if="careerSite.pending || jobFetcher.pending" :color="careerData.primaryColor" />

  <Error class="mx-auto max-w-screen-xl px-4 py-16" v-else-if="careerSite.error || jobFetcher.error" :retry="careerSite.refresh" />

  <div class="h-screen pt-32 font-sans lg:pt-40" v-else :style="{ color: careerData.textColor }">
    <PagesCareerJobHeader :cover="careerData.cover" :employer="job.employer" :title="job.title" />

    <div class="flex min-h-full">
      <div class="flex-1 bg-zinc-100" />

      <div class="w-full max-w-screen-xl lg:flex">
        <PagesCareerJobSidebar :career-slug="careerData.slug" :color="careerData.primaryColor" :job="job" @apply="apply = true" />

        <PagesCareerJobBody :job="job" :color="careerData.primaryColor" />
      </div>

      <div class="flex-1" />
    </div>

    <PagesCareerFooter class="lg:pl-80" :color="careerData.primaryColor" :employer="careerData.employer" />
  </div>

  <ClientOnly>
    <PagesCareerApplyDialog
      :career-site="careerData"
      :job="job"
      :mandatoryLetter="job?.mandatoryLetter"
      :canLetterQuestions="job?.canLetterQuestions"
      :mobility="mobility"
      title="Je postule"
      :open="apply"
      @close="apply = false"
    />

    <Notifications />
  </ClientOnly>
</template>

<style>
  .job-link {
    text-decoration-color: v-bind('careerData.primaryColor');
    transition: color 0.3s ease;
  }
  .job-link:hover {
    color: v-bind('careerData.primaryColor');
  }
</style>
